/**
 * Welche Spur gehört eine Position an? — EINE Antwort für alle Aufrufer.
 *
 * Ronni am 11.08.2026: „Ich möchte nicht alles nur auf Revolut traden. Ich
 * möchte auch manchmal Sachen probieren, die ich nicht in Wirklichkeit
 * eingehe, um zu sehen, was passiert wäre."
 *
 * Daraus werden DREI Spuren, die nie in einer Zahl vermischt werden dürfen —
 * genau der Fehler der alten PerformanceDashboardCard, die Phantom-Pläne und
 * echte Trades zu einer Bilanz addierte:
 *
 *   echt          bei Revolut wirklich gekauft. Bindet Geld, zählt ins Risiko,
 *                 erscheint in „Was ist zu tun".
 *   signal_papier ein SYSTEMSIGNAL, auf Papier mitgenommen. Bindet kein Geld,
 *                 zählt nicht ins Risiko, erscheint NICHT in der
 *                 Handlungsliste — der Cron führt Teilverkauf und Stop für
 *                 Papier-Positionen selbst aus (`paper-verwaltung.ts`), es
 *                 gibt also nichts zu tun. Genau das macht die Spur zur
 *                 sauberen Messung: Sie folgt der Regel mechanisch, ohne dass
 *                 menschliche Disziplin das Ergebnis färbt.
 *   experiment    im Papierkonto durchgespielt, OHNE Systemvorschlag. Bindet
 *                 kein Geld, zählt NICHT ins Risiko, erscheint NICHT in der
 *                 Handlungsliste — sonst konkurriert ein Versuch mit echten
 *                 Aufträgen um die Aufmerksamkeit.
 *   paper         alles übrige Papierkonto (z. B. aus Plänen entstanden).
 *
 * WARUM `signal_papier` DAZUKAM (Audit 12.08.2026, Befund 3)
 * Ronni hat PDD — ein echtes Systemsignal — auf Papier genommen. Dafür gab es
 * nur den Experiment-Weg, und der ist per Definition „ohne Systemvorschlag".
 * Sein Trade trug damit ein Etikett, das ihn falsch beschreibt, und genau die
 * Trennung, die dieses Modul schützt, war verwischt. Erst mit einer eigenen
 * Spur bleiben drei Fragen getrennt beantwortbar:
 *   Was habe ich riskiert?          → echt
 *   Was hätte das Signal gebracht?  → signal_papier
 *   Was probiere ich abseits davon? → experiment
 *
 * Warum ein eigenes Modul: `konto-spur.ts` beantwortet „Papier oder echt?"
 * für die Verwaltung. Hier geht es um die Anzeige- und Risikofrage, und die
 * hat drei Antworten statt zwei. Beide lesen dieselben zwei Felder.
 */

export type Spur = 'echt' | 'signal_papier' | 'experiment' | 'paper';

export interface SpurQuelle {
  broker_account?: string | null;
  created_by?: string | null;
}

/**
 * `|| 'paper'` statt `??`: ConvertPlanDialog hat früher einen LEEREN String
 * geschrieben, und der ist mit `??` nicht paper (siehe konto-spur.ts).
 */
export function spurVon(p: SpurQuelle): Spur {
  const konto = (p.broker_account || 'paper').toLowerCase();
  if (konto !== 'paper') return 'echt';
  if (p.created_by === 'experiment') return 'experiment';
  if (p.created_by === 'signal_papier') return 'signal_papier';
  return 'paper';
}

/** Bindet diese Position echtes Geld? Grundlage jeder Risiko-Rechnung. */
export function bindetEchtesGeld(p: SpurQuelle): boolean {
  return spurVon(p) === 'echt';
}

/**
 * Gehört sie in „Was ist zu tun"?
 *
 * NUR was echtes Geld bindet. Alles Papier — Experiment wie Signal-Papier —
 * verwaltet der Cron selbst: Teilverkauf und Stop werden dort automatisch
 * ausgeführt (`paper-verwaltung.ts`, aufgerufen im Score-Cron). Eine Zeile in
 * der Handlungsliste wäre also eine Aufforderung zu etwas, das über Nacht von
 * allein passiert — und verschwände am nächsten Morgen wieder.
 *
 * KORREKTUR vom 12.08.2026: Beim Einführen der vierten Spur stand hier kurz
 * die Begründung, `signal_papier` gehöre in die Liste, „weil es derselben
 * Regel folgt". Das stimmt — aber gerade deshalb macht es der Cron, und für
 * den Nutzer bleibt nichts übrig.
 */
export function gehoertInHandlungsliste(p: SpurQuelle): boolean {
  return spurVon(p) === 'echt';
}

export const SPUR_LABEL: Record<Spur, string> = {
  echt: 'Revolut',
  signal_papier: 'Signal auf Papier',
  experiment: 'Experiment',
  paper: 'Papier',
};

/**
 * Darf für diese Spur ein Alarm auf das Telefon?
 *
 * Nur echtes Geld. Ein Papier-Trade — auch einer aus einem Systemsignal —
 * ist zum Nachlesen da, nicht zum Wecken. Getrennt von
 * `gehoertInHandlungsliste`, weil eine Liste, die man selbst aufschlägt,
 * etwas anderes ist als eine Nachricht, die einen unterbricht.
 */
export function darfAlarmieren(p: SpurQuelle): boolean {
  return spurVon(p) === 'echt';
}
